import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/blog-posts")
            .then((r) => setPosts(r.data))
            .finally(() => setLoading(false));
    }, []);

    return (
        <main data-testid="blog-page" className="text-white pt-32 pb-24">
            <SEO
                title="Founder Stories | Purposeful, Inc."
                description="Founder stories from Purposeful, Inc. Ideas, decisions and lessons from the founders behind the book, written one conversation at a time."
                path="/founder-stories"
                keywords="founder stories, founder interviews, impact founder essays, Purposeful Inc, Substack founder stories, businesses that matter"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Blog",
                    "@id": "https://purposeful.inc/founder-stories#blog",
                    "url": "https://purposeful.inc/founder-stories",
                    "name": "Purposeful, Inc. — Founder Stories",
                    "description": "Snippets from founder interviews and essays behind the book.",
                    "author": { "@type": "Person", "name": "Chandrashekar Bhat" },
                    "publisher": { "@id": "https://purposeful.inc/#organization" },
                    "blogPost": posts.slice(0, 20).map((p) => ({
                        "@type": "BlogPosting",
                        "headline": p.title,
                        "url": p.substack_url,
                        "author": { "@type": "Person", "name": p.founder_name || "Chandrashekar Bhat" },
                        "datePublished": p.published_at || p.created_at,
                        "description": p.excerpt,
                        "image": p.cover_image_url || undefined
                    }))
                }}
                breadcrumbs={[{ name: "Home", path: "/" }, { name: "Founder Stories", path: "/founder-stories" }]}
            />

            <section className="container-x mx-auto max-w-7xl">
                <div className="overline mb-4">Founder Stories</div>
                <h1 className="font-display text-5xl md:text-6xl font-semibold tracking-tight">
                    The book, written<br />
                    <span className="text-[#D4AF37]">one story at a time.</span>
                </h1>
                <p className="mt-5 text-zinc-400 text-lg max-w-2xl">
                    Ideas, stories and lessons from the founders behind
                    Purposeful. Click any piece to read the full version on
                    Substack.
                </p>

                <div className="mt-14">
                    {loading ? (
                        <p className="text-zinc-500">Loading…</p>
                    ) : posts.length === 0 ? (
                        <div className="card-dark p-12 text-center" data-testid="blog-empty">
                            <p className="text-zinc-400">Posts will appear here as they're published on Substack.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {posts.map((p) => (
                                <a
                                    key={p.id}
                                    href={p.substack_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="blog-card"
                                    className="card-dark p-8 flex flex-col group"
                                >
                                    {p.cover_image_url && (
                                        <div className="aspect-video rounded-lg overflow-hidden mb-5 ring-1 ring-white/10">
                                            <img src={p.cover_image_url} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    )}
                                    <div className="overline">{p.founder_name || "Essay"}</div>
                                    <h2 className="font-display text-2xl mt-3 leading-tight">{p.title}</h2>
                                    <p className="text-zinc-400 text-sm mt-3 leading-relaxed line-clamp-4">{p.excerpt}</p>
                                    <div className="mt-auto pt-6 text-xs font-mono uppercase tracking-widest text-[#D4AF37] inline-flex items-center gap-2">
                                        Read on Substack <ArrowUpRight size={14} />
                                    </div>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </main>
    );
}
